import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Tooltip,
  InputAdornment,
  Divider,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Groups as GroupIcon,
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Debug: forcer l'effacement des données de démo
  console.log('🔍 DEBUG: Composant AuditLogs initialisé');
  
  // Filtres
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    search: ''
  });

  const [stats, setStats] = useState({
    totalActions: 0,
    uniqueUsers: 0,
    successRate: 0,
    recentActivity: 0
  });

  useEffect(() => {
    loadAuditLogs();
    // Actualiser toutes les minutes
    const interval = setInterval(loadAuditLogs, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, filters]);

  // Debug: log des données réellement affichées
  useEffect(() => {
    console.log('🔍 DEBUG: Logs dans le state:', logs.length);
    console.log('🔍 DEBUG: Filtered logs:', filteredLogs.length);
  }, [logs, filteredLogs]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      console.log('🔍 DEBUG: Début chargement audit logs');
      
      const data = await apiService.get('/api/admin/audit-logs', {
        limit: 100
      });

      console.log('🔍 DEBUG: Données reçues:', data);
      console.log('🔍 DEBUG: Type de data:', typeof data);
      console.log('🔍 DEBUG: data.logs existe?', !!data.logs);
      console.log('🔍 DEBUG: Nombre de logs:', data.logs?.length);

      // Mapper les données de l'API au format attendu par le composant
      const mappedLogs = (data.logs || []).map(log => {
        // Déterminer la vraie action basée sur l'URL et la méthode
        let realAction = log.action || 'UNKNOWN';
        if (log.url?.includes('/auth/login') && log.method === 'POST') {
          realAction = 'LOGIN';
        } else if (log.url?.includes('/auth/logout') && log.method === 'POST') {
          realAction = 'LOGOUT';
        } else if (log.method === 'POST' && !log.url?.includes('/auth/')) {
          realAction = 'CREATE';
        } else if (log.method === 'PUT' || log.method === 'PATCH') {
          realAction = 'UPDATE';
        } else if (log.method === 'DELETE') {
          realAction = 'DELETE';
        }

        // Extraire le nom d'utilisateur depuis requestData si disponible
        let userName = log.userName;
        let userEmail = log.userEmail;
        
        if (!userName && log.requestData?.email) {
          userEmail = log.requestData.email;
          // Extraire le nom de l'email (partie avant @)
          userName = log.requestData.email.split('@')[0];
        }
        
        if (!userName && log.userId) {
          userName = `Utilisateur ${log.userId}`;
        }

        return {
          ...log,
          // Mapping corrigé
          action: realAction,
          entityType: log.entityType || 'UNKNOWN',
          success: log.success !== undefined ? log.success : log.statusCode >= 200 && log.statusCode < 300,
          method: log.method || 'GET',
          url: log.url || '',
          statusCode: log.statusCode || 200,
          userName: userName || 'Utilisateur anonyme',
          userEmail: userEmail || 'Non spécifié',
          // Mapping pour la compatibilité
          severity: log.success ? 'info' : 'error',
          category: getActionCategory(realAction),
          details: log.url || realAction || 'Action inconnue'
        };
      });

      console.log('🔍 DEBUG: Données mappées:', mappedLogs.slice(0, 2));
      
      setLogs(mappedLogs);
      setStats(data.stats || {});
      setError('');
      console.log('🔍 DEBUG: Chargement terminé avec succès');
      console.log('🔍 DEBUG: État logs après setLogs:', mappedLogs.slice(0, 2));
    } catch (error) {
      console.error('🔍 DEBUG: Erreur capturée:', error);
      console.error('🔍 DEBUG: Type erreur:', typeof error);
      console.error('🔍 DEBUG: Message:', error.message);
      console.error('🔍 DEBUG: Stack:', error.stack);
      setError(`Erreur API: ${error.message}`);
      // Désactivé temporairement pour debug
      // loadDemoData();
    } finally {
      setLoading(false);
    }
  };

  // Fonction helper pour mapper l'action vers une catégorie
  const getActionCategory = (action) => {
    if (!action) return 'other';
    
    const actionUpper = action.toUpperCase();
    if (actionUpper.includes('LOGIN') || actionUpper.includes('AUTH')) return 'auth';
    if (actionUpper.includes('CREATE') || actionUpper.includes('UPDATE') || actionUpper.includes('DELETE')) return 'data';
    if (actionUpper.includes('EMAIL') || actionUpper.includes('NOTIFICATION')) return 'communication';
    if (actionUpper.includes('SCHEDULE') || actionUpper.includes('PLANNING')) return 'schedule';
    
    return 'other';
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Filtre par date de début
    if (filters.startDate) {
      filtered = filtered.filter(log => log.timestamp >= filters.startDate);
    }

    // Filtre par date de fin
    if (filters.endDate) {
      const endOfDay = new Date(filters.endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log => new Date(log.timestamp) <= endOfDay);
    }

    // Filtre par recherche textuelle
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(log => 
        log.userEmail?.toLowerCase().includes(searchLower) ||
        log.userName?.toLowerCase().includes(searchLower) ||
        log.action?.toLowerCase().includes(searchLower) ||
        log.entityType?.toLowerCase().includes(searchLower) ||
        log.url?.toLowerCase().includes(searchLower) ||
        log.ip?.includes(searchLower) ||
        JSON.stringify(log.requestData || {}).toLowerCase().includes(searchLower)
      );
    }

    setFilteredLogs(filtered);
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'CREATE': <PersonIcon />,
      'UPDATE': <SettingsIcon />,
      'DELETE': <SecurityIcon />,
      'LOGIN': <PersonIcon />,
      'LOGOUT': <PersonIcon />,
      'SEND_EMAIL': <EmailIcon />
    };
    return iconMap[action] || <AssignmentIcon />;
  };

  const getActionColor = (action, success) => {
    if (!success) return 'error';
    
    const colorMap = {
      'CREATE': 'success',
      'UPDATE': 'warning',
      'DELETE': 'error',
      'LOGIN': 'info',
      'LOGOUT': 'default',
      'SEND_EMAIL': 'primary'
    };
    return colorMap[action] || 'default';
  };

  const getEntityTypeIcon = (entityType) => {
    const iconMap = {
      'TECHNICIAN': <PersonIcon />,
      'TEAM': <GroupIcon />,
      'ASTREINTE': <AssignmentIcon />,
      'USER': <PersonIcon />,
      'EMAIL': <EmailIcon />,
      'SETTINGS': <SettingsIcon />,
      'AUTH': <SecurityIcon />
    };
    return iconMap[entityType] || <VisibilityIcon />;
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'dd/MM HH:mm', { locale: fr });
    } catch {
      return timestamp;
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const toggleRowExpansion = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon />
          Logs d'Audit
        </Typography>
        <Tooltip title="Actualiser">
          <IconButton onClick={loadAuditLogs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Statistiques rapides */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Actions Totales
              </Typography>
              <Typography variant="h5">
                {stats.totalActions}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Utilisateurs Actifs
              </Typography>
              <Typography variant="h5">
                {stats.uniqueUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Taux de Réussite
              </Typography>
              <Typography variant="h5" color="success.main">
                {stats.successRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Activité (24h)
              </Typography>
              <Typography variant="h5">
                {stats.recentActivity}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon />
          Filtres
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date début"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Date fin"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Recherche (email, nom, action, IP...)"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table des logs */}
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {filteredLogs.length} entrée(s) d'audit {filters.startDate || filters.endDate || filters.search ? '(filtrées)' : ''}
          </Typography>
        </Box>
        
        <TableContainer sx={{ maxHeight: 600, width: '100%', overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: '900px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '100px' }}>Date</TableCell>
                <TableCell sx={{ width: '120px' }}>Utilisateur</TableCell>
                <TableCell sx={{ width: '90px' }}>Action</TableCell>
                <TableCell sx={{ width: '80px' }}>Type</TableCell>
                <TableCell sx={{ width: '80px' }}>Statut</TableCell>
                <TableCell sx={{ width: '100px' }}>IP</TableCell>
                <TableCell sx={{ width: '50px' }}>Détails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log, index) => (
                <React.Fragment key={index}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" noWrap title={`${log.userName} (${log.userEmail})`}>
                        {log.userName || log.userEmail?.split('@')[0] || 'Inconnu'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getActionIcon(log.action)}
                        label={log.action}
                        color={getActionColor(log.action, log.success)}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getEntityTypeIcon(log.entityType)}
                        <Typography variant="body2">
                          {log.entityType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.success ? 'Succès' : 'Échec'}
                        color={log.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" noWrap title={log.ip}>
                        {log.ip?.replace('::ffff:', '')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Voir les détails">
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(index)}
                        >
                          {expandedRow === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0 }}>
                      <Collapse in={expandedRow === index}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                Informations de la requête
                              </Typography>
                              <Stack spacing={1}>
                                <Typography variant="body2">
                                  <strong>Méthode:</strong> {log.method}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>URL:</strong> {log.url}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Code de statut:</strong> {log.statusCode}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Entité ID:</strong> {log.entityId || 'N/A'}
                                </Typography>
                              </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>
                                {log.action === 'DELETE' ? 'Élément supprimé' : 'Données de la requête'}
                              </Typography>
                              <Paper sx={{ p: 1, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100' }}>
                                <pre style={{ margin: 0, fontSize: '0.75em', fontFamily: 'monospace' }}>
                                  {log.requestData 
                                    ? JSON.stringify(log.requestData, null, 2)
                                    : 'Aucune donnée'
                                  }
                                </pre>
                              </Paper>
                              {log.action === 'DELETE' && log.responseData?.deletedItem && (
                                <>
                                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                    Détails de l'élément supprimé
                                  </Typography>
                                  <Paper sx={{ p: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                                    <Typography variant="body2">
                                      <strong>Nom:</strong> {log.responseData.deletedItem.name}
                                    </Typography>
                                    {log.responseData.deletedItem.description && (
                                      <Typography variant="body2">
                                        <strong>Description:</strong> {log.responseData.deletedItem.description}
                                      </Typography>
                                    )}
                                    <Typography variant="body2">
                                      <strong>ID:</strong> {log.responseData.deletedItem.id}
                                    </Typography>
                                  </Paper>
                                </>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              
              {filteredLogs.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      Aucun log d'audit trouvé
                      {(filters.startDate || filters.endDate || filters.search) && ' pour les filtres appliqués'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default AuditLogs;
